import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import Flag from '@mui/icons-material/Flag'
import moment from 'moment'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import S3Document from '../../../../components/images/S3Document'
import { GlobalContext } from '../../../../App.js'

const YesNoChip = ({ value, goodWhen = false }) => (
  <Chip
    size="small"
    label={value ? 'Yes' : 'No'}
    color={value === goodWhen ? 'success' : 'warning'}
    variant="outlined"
  />
)

const ReviewSection = ({ title, children }) => (
  <Stack gap={1}>
    <Typography variant="subtitle2" fontWeight={600}>
      {title}
    </Typography>
    {children}
    <Divider sx={{ my: 1 }} />
  </Stack>
)

// Full read-only view of one screening - also used by the occupational
// health dashboard's review popup
export const SubmissionReview = ({ submissionID }) => {
  const [loading, setLoading] = useState(true)
  const [submission, setSubmission] = useState(null)

  useEffect(() => {
    API.get(`/occupational-health/${submissionID}`)
      .then((res) => {
        if (res.status === 200) setSubmission(res.data)
      })
      .finally(() => setLoading(false))
  }, [submissionID])

  if (loading) return <LinearProgress />
  if (!submission) return <Alert severity="error">Could not load submission.</Alert>

  const b = submission.biometrics ?? {}

  return (
    <Stack gap={1}>
      <Typography variant="title3" fontWeight={700}>
        Occupational Health Screening —{' '}
        {moment(submission.submittedAt).format('Do MMM YYYY')}
      </Typography>

      {submission.flags?.length > 0 ? (
        <Alert severity="warning" icon={<Flag />}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {submission.flags.length} item(s) flagged for review:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {submission.flags.map((flag) => (
              <li key={flag}>
                <Typography variant="body2">{flag}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      ) : (
        <Alert severity="success">No answers were flagged for review.</Alert>
      )}

      <ReviewSection title="Height, Weight & BMI">
        <Stack direction="row" gap={3} flexWrap="wrap">
          <Typography variant="body2">
            Height: <strong>{b.heightCm ?? '—'} cm</strong>
          </Typography>
          <Typography variant="body2">
            Weight: <strong>{b.weightKg ?? '—'} kg</strong>
          </Typography>
          <Typography variant="body2">
            Calculated BMI: <strong>{b.bmi ?? '—'}</strong>
          </Typography>
        </Stack>
      </ReviewSection>

      <ReviewSection title="GP / Surgery">
        <Typography variant="body2" whiteSpace="pre-wrap">
          {submission.gpDetails || 'Not provided'}
        </Typography>
      </ReviewSection>

      <ReviewSection title="General Health">
        <Stack gap={0.5}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2">Disability:</Typography>
            <YesNoChip value={submission.disability?.has === true} />
          </Stack>
          {submission.disability?.has && (
            <Typography variant="body2" color="text.secondary">
              {submission.disability.details || 'No details given'}
            </Typography>
          )}
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2">Allergies:</Typography>
            <YesNoChip value={submission.allergies?.has === true} />
          </Stack>
          {submission.allergies?.has && (
            <Typography variant="body2" color="text.secondary">
              {submission.allergies.details || 'No details given'}
            </Typography>
          )}
          <Typography variant="body2">
            Regular prescribed medications:{' '}
            {submission.medications || 'None listed'}
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2">Currently pregnant:</Typography>
            <YesNoChip value={submission.pregnant === true} />
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2">
              Wears corrective lenses to drive:
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={submission.correctiveLenses ? 'Yes' : 'No'}
            />
          </Stack>
        </Stack>
      </ReviewSection>

      <ReviewSection title="Medical Conditions">
        {submission.conditions?.length > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {submission.conditions.map((condition) => (
              <Chip
                key={condition}
                size="small"
                color="warning"
                variant="outlined"
                label={condition}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            None declared
          </Typography>
        )}
      </ReviewSection>

      <ReviewSection title="Immunisations">
        <Stack gap={0.5}>
          {(submission.immunisations ?? []).map((item) => (
            <Stack
              key={item.label}
              direction="row"
              gap={1}
              alignItems="center"
            >
              <Typography variant="body2">{item.label}:</Typography>
              <YesNoChip value={item.immunised === true} goodWhen={true} />
            </Stack>
          ))}
        </Stack>
        {submission.evidence?.length > 0 && (
          <Stack gap={1} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Uploaded evidence:
            </Typography>
            {submission.evidence.map((file) => (
              <S3Document
                key={file.s3key}
                item={file.s3key}
                title={file.title}
                showAsRow
              />
            ))}
          </Stack>
        )}
      </ReviewSection>

      <ReviewSection title="Fitness">
        <Stack gap={0.5}>
          {(submission.fitness ?? []).map((item) => (
            <Stack
              key={item.label}
              direction="row"
              gap={1}
              alignItems="center"
            >
              <Typography variant="body2">{item.label}:</Typography>
              <YesNoChip value={item.able === true} goodWhen={true} />
            </Stack>
          ))}
        </Stack>
      </ReviewSection>

      <ReviewSection title="Individual COVID-19 Risk Assessment">
        {(submission.covidRisks ?? []).filter((r) => r.selected).length > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {(submission.covidRisks ?? [])
              .filter((r) => r.selected)
              .map((risk) => (
                <Chip
                  key={risk.label}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={risk.auto ? `${risk.label} (auto)` : risk.label}
                />
              ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No risks identified
          </Typography>
        )}
      </ReviewSection>

      {submission.customAnswers?.length > 0 && (
        <ReviewSection title="Additional Questions">
          <Stack gap={0.5}>
            {submission.customAnswers.map((item) => (
              <Stack
                key={item.question}
                direction="row"
                gap={1}
                alignItems="center"
              >
                <Typography variant="body2">{item.question}</Typography>
                <Chip size="small" variant="outlined" label={item.answer} />
              </Stack>
            ))}
          </Stack>
        </ReviewSection>
      )}

      <Typography variant="body2" color="text.secondary">
        Health & safety declaration agreed:{' '}
        {submission.healthSafetyAgreed ? 'Yes' : 'No'}
      </Typography>
    </Stack>
  )
}

const OccupationalHealthTab = ({ employee }) => {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [submissions, setSubmissions] = useState([])
  const [viewingID, setViewingID] = useState(null)
  const [requesting, setRequesting] = useState(false)

  const { setToast } = useContext(GlobalContext)

  const employeeID = employee?.employee?._id

  const load = () => {
    if (!employeeID) return
    API.get(`/occupational-health/employee/${employeeID}`)
      .then((res) => {
        if (res.status === 200) {
          setStatus(res.data.status)
          setSubmissions(res.data.submissions)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [employeeID])

  const requestNew = () => {
    setRequesting(true)
    API.post(`/occupational-health/employee/${employeeID}/request`)
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'New Screening Requested' })
          load()
        }
      })
      .finally(() => setRequesting(false))
  }

  if (loading) return <LinearProgress />

  return (
    <Stack gap={2}>
      {status?.enabled === false && (
        <Alert severity="info">
          Occupational health screening is disabled in your organisation's
          configuration.
        </Alert>
      )}

      {status?.enabled !== false && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1}
        >
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            {status?.due ? (
              <Chip
                color="error"
                label={
                  status.requested
                    ? 'New screening requested'
                    : 'Never completed'
                }
              />
            ) : (
              <Chip color="success" label="Screening complete" />
            )}
            {status?.lastSubmittedAt && (
              <Typography variant="body2" color="text.secondary">
                Last completed{' '}
                {moment(status.lastSubmittedAt).format('Do MMM YYYY')} —
                confirmed or updated at each appraisal
              </Typography>
            )}
          </Stack>
          <Button
            variant="outlined"
            disabled={requesting || status?.requested}
            onClick={requestNew}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {status?.requested
              ? 'Screening Requested'
              : requesting
                ? 'Requesting...'
                : 'Request New Screening'}
          </Button>
        </Stack>
      )}

      {submissions.length === 0 ? (
        <Typography color="text.secondary">
          No occupational health screenings recorded yet.
        </Typography>
      ) : (
        <Stack gap={1}>
          {submissions.map((submission) => (
            <Card key={submission._id} variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    Completed{' '}
                    {moment(submission.submittedAt).format('Do MMM YYYY')}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center">
                    {submission.flags?.length > 0 ? (
                      <Chip
                        size="small"
                        color="warning"
                        icon={<Flag />}
                        label={`${submission.flags.length} flagged`}
                      />
                    ) : (
                      <Chip size="small" color="success" label="No flags" />
                    )}
                    <Button
                      size="small"
                      onClick={() => setViewingID(submission._id)}
                    >
                      View
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <BigModal open={!!viewingID} onClose={() => setViewingID(null)}>
        <Stack padding={2}>
          {viewingID && <SubmissionReview submissionID={viewingID} />}
        </Stack>
      </BigModal>
    </Stack>
  )
}

export default OccupationalHealthTab
